import moment from "moment";

import { Unit } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Unit";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { DateTime } from "@/Common/Helpers/DateTime";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { MyleDSQMedicationItem } from "@/Common/Models/DSQ/MyleDSQMedicationItem";
import { Guid } from "@/Common/Models/Guid";
import { PatientMedicationAction, PrescriptionMedicationStatus } from "@/Common/Models/Patient/PatientEnums";
import { DiscontinueReason } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { resolveMedication } from "@/Patient/Api/MedicationApiClient";
import { MedicationSearchListItem } from "@/Patient/Components/Medication/SearchMedication";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { ISortableMedicalItem } from "../MedicalSummary/MedicalItem";
import { MedicalItemMedicationAndPrescription } from "../MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicalItemMedicationAndPrescriptionDosage } from "../MedicalSummary/MedicalItemMedicationAndPrescriptionDosage";
import { EnumPatientSummaryMedicationDisplayStatus } from "../MedicalSummary/MedicalItemMedicationDisplayStatus";
import { MedicationLegalStatusHelper } from "./Enums/MedicationLegalStatus";
import { MedicationTerm } from "./Enums/MedicationTerm";
import { MedicationResponse } from "./ResponseModels/MedicationResponse";
import { DosageDisplayOptions } from "./DosageDisplayOptions";

enum MedicationDosageDisplayMode {
    Quantity,
    EndDate,
    Duration
}

enum MedicationDosageDisplayModeDurationUnit {
    Days,
    Months,
    Weeks
}

class MedicationModel implements ISortableMedicalItem {
    public IsLoading: boolean;
    public IsInEditMode: boolean;
    public EMRTemplateSortIndex: number;

    public Model: MedicalItemMedicationAndPrescription;
    public PrescriptionTab_OriginalMedication: MedicationModel;

    constructor(model: MedicalItemMedicationAndPrescription) {
        this.IsLoading = false;
        this.IsInEditMode = true;
        this.Model = model;

        if (model != null) {
            this.CreateFakeDosage();
            this.EMRTemplateSortIndex = this.Model.EMRTemplateSortIndex;
        }
    }

    public isDSQSynced(): boolean {
        return this.Model.DsqId != null && this.Model.DsqId != "" && this.Model.DsqId != undefined;
    }

    public mortDeSilverlight = new Date(2018, 10, 12);
    public isCeasedMedInSpecificPrescription(currentPrescriptionId: string, currentPrescriptionDate: Date): boolean {
        if (currentPrescriptionId == this.Model.CeasePrescriptionId)
            return this.IsCeased();
        else if (this.Model.CeaseDate != null && moment(this.Model.CeaseDate).isSameOrBefore(this.mortDeSilverlight) && moment(this.Model.CeaseDate).isSameOrBefore(moment(currentPrescriptionDate))) // Find the exact data at which we stopped using silverlight with CeasePrescriptionId Null and thread  CeasePrescriptionId Null has ceased is current prescription only for stuff that was ceased before this date
            return this.IsCeased();
        else
            return false;
    }

    public ClearIdentificationFields(): MedicationModel {
        this.Model.Name = "";
        this.Model.DIN = null;
        this.Model.CodeGen = null;
        this.Model.Description = null;
        this.Model.DrugStrength = null;
        this.Model.StrengthUnit = null;
        this.Model.LegalStatus = null;
        return this;
    }

    public IsNarcotic(): boolean {
        return MedicationLegalStatusHelper.isNarcotic(this.Model.LegalStatus);
    }

    // This function was duplicated in src/Flowsheets/Helpers/MedicationDirectivesHelper so it can be used independently of the MedicationModel
    public EffectiveEndDate(): Date {
        if (this.Model.CeaseDate != null) {
            let date: Date = new Date(this.Model.CeaseDate.toString());
            return date;
        }
        else if (this.Model.RefillEndDate != null) {
            let date: Date = new Date(this.Model.RefillEndDate.toString());
            return date;
        }
        else
            return null;
    }

    public EMRTemplateSortIndexSet(value: number): void {
        this.EMRTemplateSortIndex = value;
        this.Model.EMRTemplateSortIndex = value;
    }

    public RecalculateRefillEndDate(): void {
        if (this.Model.TotalDurationManual == null) {
            this.Model.RefillEndDate = null; // No duration means End Date is (null / infinite)
        }
        else if (this.Model.RefillStartDate != null) {
            const medicationDuration = moment.duration(this.TicksToDays(this.Model.TotalDurationManual), "days");
            this.Model.RefillEndDate = moment(this.Model.RefillStartDate).add(medicationDuration).toDate();
        }
    }

    public SetStartDate(startDate: Date): void {
        this.Model.RefillStartDate = startDate;
        this.RecalculateTotalDuration();
    }

    public SetEndDate(endDate: Date, changeDosageModeOnNullEndDate: boolean = false): void {
        if (endDate != null && endDate.getFullYear() < 3000 && endDate.getFullYear() > 1900)
            this.Model.RefillEndDate = endDate;

        const lastDosage = this.GetLastDosage();
        if (!endDate && changeDosageModeOnNullEndDate) {
            if (lastDosage.DisplayMode != MedicationDosageDisplayMode.EndDate)
                this.SetModeForDosage(lastDosage.Id, MedicationDosageDisplayMode.EndDate);

            this.Model.TotalDurationManual = null;
        }
        else if (this.Model.RefillStartDate == null) {
            if (lastDosage.DisplayMode != MedicationDosageDisplayMode.EndDate)
                this.RecalculateTotalDuration();

            this.Model.RefillStartDate = moment(endDate).subtract(moment.duration(this.TicksToDays(this.Model.TotalDurationManual), "days")).toDate();
        }
        this.RecalculateTotalDuration();
    }

    public CreateFakeDosage(): void {
        if (!this.Model.Dosages?.count()) {
            this.Model.Route = null;

            this.Model.Dosages = Immutable.List([]);
            const perDoseLookup = cacheStore.FindLookupByLookupCode("DU.PerDose", "DosageType");

            const tmpDosage = new MedicalItemMedicationAndPrescriptionDosage({
                IsFakeDosage: true,
                Duration: this.Model.TotalDurationManual,
                RankPosition: 0,
                IfNeeded: false,
                MedicationId: this.Model.Id,
                DosageTypeLookupId: perDoseLookup.LookupID,
                DosageTypeLookupValue: perDoseLookup.GetValue()
            });

            this.Model.Dosages = Immutable.List([tmpDosage]);
        }
    }

    public Clone(newId: boolean = false, resetAsActive: boolean = false, keepPendingStatuses: boolean = false, fromFavoriteAndKeepInstructions: boolean = false): MedicationModel {
        const medicationClone: MedicalItemMedicationAndPrescription = new MedicalItemMedicationAndPrescription(JSON.parse(JSON.stringify(this.Model)));
        medicationClone.Dosages = Immutable.List(medicationClone.Dosages.map(p => new MedicalItemMedicationAndPrescriptionDosage(p))); // Recreate immutables after cloning

        if (newId == true) {
            medicationClone.Id = GuidHelper.NewGuid();
            medicationClone.RefillStartDate = null;
            medicationClone.CommitDate = null;
            medicationClone.IsCommitted = false;

            // Reset DSQ Stuff
            // medicationClone.isDSQMed_NotImported <= Keep this one, it refers to the source of the clone
            medicationClone.DsqEventId = null;
            medicationClone.DsqId = null;
            medicationClone.DSQNumberOfRefillRemaining = null;
            medicationClone.DsqOIDVersion = null;
            medicationClone.DsqSyncDateString = null;
            medicationClone.LastDSQPickupDate = null;
            medicationClone.LastDSQSyncDate = null;

            if (resetAsActive == true) {
                medicationClone.RefillStartDate = moment(Date.now()).toDate();
                medicationClone.RefillEndDate = null;
                medicationClone.CeaseDate = null;
                medicationClone.CeaseEmployeeId = null;
                medicationClone.CeaseEncounterId = null;
                medicationClone.CeasePrescriptionId = null;
                medicationClone.CeaseReason = null;
                medicationClone.DiscontinueReasonLookupId = null;
                if (!fromFavoriteAndKeepInstructions)
                    medicationClone.Instruction = null;
                medicationClone.PrescriptionStatusInt = 0;
            }
        }

        const clonedMed = new MedicationModel(medicationClone);

        if (keepPendingStatuses) {
            // Reinit pending Status //Add Child Meds
            if (this.PrescriptionTab_OriginalMedication != null) {
                clonedMed.PrescriptionTab_OriginalMedication = this.PrescriptionTab_OriginalMedication.Clone(false, false);
            }
        }

        return clonedMed;
    }

    public Renew(): MedicationModel {
        const medicationClone = this.Clone(true, true, undefined, true); // we use undefined here to let the default value of that parameter be taken
        medicationClone.Model.PrescriptionTab_IsRenew = true;
        medicationClone.Model.OriginalMedicationId = this.Model.Id;

        if (!this.IsNarcotic() && this.Model.NumberOfRefill == null && this.Model.IsPast == true)
            medicationClone.Model.NumberOfRefill = sessionStore.getCurrentEmployee().Model.DefaultRefill;

        if (medicationClone.Model.TotalDurationManual == null || medicationClone.Model.TotalDurationManual < this.DaysToTicks(1)) {
            medicationClone.Model.TotalDurationManual = this.DaysToTicks(30);
            medicationClone.mutateDosage(medicationClone.GetLastDosage().Id, dosage => {
                if (dosage.Duration == null || dosage.Duration <= this.DaysToTicks(1))
                    dosage.Duration = this.DaysToTicks(30);
            });
        }

        if (this.IsNarcotic()) {
            medicationClone.mutateDosage(medicationClone.GetLastDosage().Id, dosage => {
                dosage.Duration = 0;
            });
        }

        medicationClone.IsInEditMode = false;

        return medicationClone;
    }

    public SetRefillNumber(refillNumber: number): void {
        if (!this.IsNarcotic()) {
            this.Model.NumberOfRefill = refillNumber;
            this.RecalculateTotalDuration();
        }
    }

    public GetConflictingActiveMedFollowingDSQRenew(): string[] {
        return this.Model.DSQRenew_MyleActiveMedicationIdsConflic ? this.Model.DSQRenew_MyleActiveMedicationIdsConflic : [];
    }

    public static FromDefaultModel(isEmptyRx: boolean, isPast: boolean): MedicationModel {
        const medication = new MedicalItemMedicationAndPrescription({
            Id: GuidHelper.NewGuid(),
            TotalDurationManual: 864000000000 * 30,
            Route: "PO",
            NumberOfRefill: sessionStore.getCurrentEmployee().Model.DefaultRefill,
            IsSubstituable: true,
            PrescriptionTab_IsEmptyRx: isEmptyRx,
            IsPast: isPast,
            PrescriptionStatusInt: 0,
            RefillStartDate: (isPast == true) ? null : moment(Date.now()).toDate(),
            Name: ""
        });

        return new MedicationModel(medication);
    }

    public static FromMyleDSQMedicationItem(dsqMed: MyleDSQMedicationItem, IsImporting: boolean = false): MedicationModel {
        const dto: MedicationModel = MedicationModel.FromDefaultModel(false, true);
        dto.Model.isDSQMed_NotImported = !IsImporting;

        dto.Model.Route = null;
        dto.Model.LastDSQSyncDate = moment(Date.now()).toDate();
        dto.Model.NumberOfRefill = dsqMed.NumberOfRefills;

        dto.Model.RefillStartDate = new Date(dsqMed.StartDate);
        dto.SetEndDate(new Date(dsqMed.EndDate));

        dto.Model.DIN = dsqMed.DIN;
        dto.Model.CodeGen = dsqMed.ResolvedCodeGen;
        dto.Model.LegalStatus = dsqMed.ResolvedLegalStatus;

        dto.Model.Name = dsqMed.NameStrength.toTitleCase(); // NameStrength **Seems like DSQ returns it concatenated;

        dto.Model.DrugStrength = "";

        dto.Model.Instruction = dsqMed.Dosage;
        dto.Model.MedicationNumber = 0;
        dto.Model.IsSubstituable = true;
        dto.Model.MDPracticianId = null;

        dto.Model.LastDSQPickupDate = new Date(dsqMed.LastPickupDate);
        dto.Model.DSQNumberOfRefillRemaining = dsqMed.RefillsRemaining;
        dto.Model.DsqId = dsqMed.DSQMedicationID;
        dto.Model.DsqOIDVersion = dsqMed.DSQOIDVersion;

        dto.Model.PrescriptionStatusInt = 0;
        dto.Model.CommitDate = moment(Date.now()).toDate();

        if (dsqMed.TotalQuantity != null && dsqMed.TotalQuantity != "" && dsqMed.TotalQuantity != "0" && isNaN(Number(dsqMed.TotalQuantity)) != true) {
            const firstDosage = dto.Model.Dosages.first();
            firstDosage.DisplayMode = MedicationDosageDisplayMode.Quantity;

            if (dto.Model.NumberOfRefill != null && dto.Model.NumberOfRefill > 0 && !isNaN(dsqMed.NumberOfRefills))
                firstDosage.DosageServedQuantity = Math.round(Number(dsqMed.TotalQuantity) / dsqMed.NumberOfRefills).toString();
            else
                firstDosage.DosageServedQuantity = dsqMed.TotalQuantity;

            dto.Model.Dosages = Immutable.List([firstDosage]);
        }

        dto.Model.EMRTemplateIsVisibleInCPP = true;

        return dto;
    }

    public FromResponseLightUpdate(responseLight: MedicationResponse, IsPast: boolean): void {
        this.Model.Name = responseLight.Name;
        this.Model.DrugStrength = responseLight.Strength;
        this.Model.Form = responseLight.Form;
        this.Model.LegalStatus = responseLight.LegalStatus;
        this.Model.CreatorEmployeeId = cacheStore.GetCurrentEmployee().ContactFacet_id;
        this.Model.Instruction = responseLight.Instruction ?? "";
        this.Model.IsPast = IsPast;
        this.Model.PrescriptionStatusInt = 0;
        this.Model.DIN = responseLight.Din;
    }

    public FromResponseFullUpdate(responseFull: MedicationResponse): void {
        const item = this;

        // OverWrite, just in case
        item.Model.LegalStatus = responseFull.LegalStatus;
        item.Model.CodeGen = responseFull.CodeGen;
        const nonRenewableNarcotics = MedicationLegalStatusHelper.isNarcotic(item.Model.LegalStatus);

        item.Model.DIN = responseFull.Din;
        item.Model.Route = responseFull.Route;
        item.Model.IsSubstituable = true;

        if (cacheStore.ClinicSettings.EnableLongTermMedication) {
            item.Model.IsShortTerm = responseFull.Term === MedicationTerm.ShortTerm;
            item.Model.IsLongTerm = responseFull.Term === MedicationTerm.LongTerm;
        }

        const longTermMedicationDurationInDays = cacheStore.GetCurrentEmployee().LongTermMedicationDurationInDays;
        // Renouvellement
        const refills = sessionStore.getCurrentEmployee().Model.DefaultRefill;
        const longTermRefillNumber = sessionStore.getCurrentEmployee().Model.LongTermMedicationDefaultRefillNumber;

        if (nonRenewableNarcotics)
            item.Model.NumberOfRefill = 0;
        else if (longTermMedicationDurationInDays > 0 && this.isLongTerm())
            item.Model.NumberOfRefill = longTermRefillNumber ?? 0;
        else if (responseFull.RenewalInfo == "DEFAULT")
            item.Model.NumberOfRefill = refills == null ? 12 : refills;
        else if (responseFull.RenewalInfo == "NR")
            item.Model.NumberOfRefill = 0;
        else if (responseFull.RenewalInfo && !isNaN(Number(responseFull.RenewalInfo)))
            item.Model.NumberOfRefill = Number(responseFull.RenewalInfo);
        else
            item.Model.NumberOfRefill = refills == null ? 12 : refills;

        // -------------
        if (longTermMedicationDurationInDays > 0 && this.isLongTerm())
            responseFull.Duration = longTermMedicationDurationInDays;

        item.Model.Dosages = Immutable.List([]);
        const tmpDosage = new MedicalItemMedicationAndPrescriptionDosage({
            Duration: responseFull.Duration != null && !nonRenewableNarcotics
                ? responseFull.Duration * 864000000000
                : 0,
            RankPosition: 0,
            IfNeeded: responseFull.IsNeeded,
            DosageUnit: responseFull.Unit,
            DosageString: responseFull.Dose != null ? responseFull.Dose : "1"
        });

        tmpDosage.DosageServedDays = "";
        tmpDosage.DosageServedFrequency = "";
        tmpDosage.DosageFrequency = responseFull.FrequencyCode;
        tmpDosage.MedicationId = item.Model.Id;

        if ((tmpDosage.Duration == 0 && responseFull.Quantity != null) && responseFull.Quantity != "0" || nonRenewableNarcotics) {
            tmpDosage.DisplayMode = MedicationDosageDisplayMode.Quantity;

            const QtyClean = responseFull.Quantity.replace(",", ".");
            if (!isNaN(Number(QtyClean)))
                tmpDosage.DosageServedQuantity = QtyClean;
        }

        else if (tmpDosage.Duration == 0)
            tmpDosage.Duration = 864000000000 * 30; // 30 days by default no there is no duration or quantity indication

        const perDoseLookup = cacheStore.FindLookupByLookupCode("DU.PerDose", "DosageType");
        tmpDosage.DosageTypeLookupId = perDoseLookup.LookupID;
        tmpDosage.DosageTypeLookupValue = perDoseLookup.GetValue();

        item.Model.Dosages = Immutable.List([tmpDosage]);

        item.RecalculateTotalDuration();
        item.recalculateDispensationQuantities();
        item.IsLoading = false;
    }

    public static getMedicationStatusList(hasEmpty: boolean): ComboBoxDropDownItem[] {
        const defaultDropDownItemList = [
            new ComboBoxDropDownItem(PrescriptionMedicationStatus.Interrupted, MedicationModel.PrescriptionStatusIntToValue(1), 0, null), // Prescription Interupt
            new ComboBoxDropDownItem(PrescriptionMedicationStatus.Augmented, MedicationModel.PrescriptionStatusIntToValue(2), 1, null), // Prescription Augment and Replace
            new ComboBoxDropDownItem(PrescriptionMedicationStatus.Reduced, MedicationModel.PrescriptionStatusIntToValue(3), 2, null) // Prescription Reduce and Replace
            // Need to suppport it better, new ComboBoxDropDownItem("4", MedicationModel.PrescriptionStatusIntToValue(4), 3, null), //Prescription Suspended and Replace
        ];

        if (!hasEmpty) {
            return defaultDropDownItemList;
        }

        return [
            new ComboBoxDropDownItem(null, "", -1, null),
            ...defaultDropDownItemList
        ];
    }

    public static getDiscontinueReasonList(): ComboBoxDropDownItem[] {
        return cacheStore
            .getDiscontinueReasonLookups()
            .map(x => x.ToComboboxItem());
    }

    public static getDefaultDiscontinueReason(): string {
        return cacheStore
            .getDiscontinueReasonLookups()
            .filter(x => x.LookupCode == DiscontinueReason.Other)
            .firstOrDefault()
            .LookupID;
    }

    public RecalculateTotalDuration(): void {
        if (this.GetLastDosage().DisplayMode == MedicationDosageDisplayMode.EndDate) {
            if (!this.Model?.RefillEndDate)
                this.Model.TotalDurationManual = null;
            else if (this.Model?.RefillStartDate)
                this.Model.TotalDurationManual = this.DaysToTicks(LuxonDateHelper.dateDiff(this.Model.RefillStartDate, this.Model.RefillEndDate, "days"));
        }
        else {
            let totalduration: number = 0;

            let refills = 1;
            if (!isNaN(Number(this.Model.NumberOfRefill)))
                refills += Number(this.Model.NumberOfRefill);

            this.Model.Dosages.forEach((dosage, idx) => {
                let dosageDuration = 0;
                if (dosage.DisplayMode == MedicationDosageDisplayMode.Quantity) {
                    if (!isNaN(Number(dosage.DosageServedQuantity)) && !isNaN(Number(dosage.DosageString)) && Number(dosage.DosageString) != 0)
                        dosageDuration += Math.round((Number(dosage.DosageServedQuantity) * MyleHelper.MultiplicateurSelonFrequence(dosage.DosageFrequency)) / Number(dosage.DosageString)) * 864000000000;
                    else
                        return;
                }
                else {
                    // Round to the unit used
                    if (dosage.DisplayModeDurationUnit == MedicationDosageDisplayModeDurationUnit.Weeks)
                        dosage.Duration = this.DaysToTicks(Math.round(this.TicksToDays(dosage.Duration) / 7) * 7);
                    else if (dosage.DisplayModeDurationUnit == MedicationDosageDisplayModeDurationUnit.Months)
                        dosage.Duration = this.DaysToTicks(Math.round(this.TicksToDays(dosage.Duration) / 30) * 30);

                    dosageDuration += dosage.Duration;
                }

                totalduration += this.Model.Dosages.count() == idx + 1
                    ? dosageDuration * refills
                    : dosageDuration; // # of refills applies to last dosage duration
            });

            const refreshEndDate = this.Model.TotalDurationManual != totalduration;

            if (totalduration != 0)
                this.Model.TotalDurationManual = totalduration;

            if (refreshEndDate)
                this.RecalculateRefillEndDate();
        }
    }

    public recalculateDispensationQuantities(): void {
        if (!settingsStore.configuration.get("ShouldShowAndCalculateMedicationDispensationQuantities") || this.IsNarcotic())
            return;

        if (this.Model.Dosages.some(p => p.DosageTypeLookupId !== cacheStore.FindLookupByLookupCode("DU.PerDose", "DosageType").LookupID))
            return this.resetDispensationQuantities();

        const lastDosage = this.Model.Dosages.last();

        if (!lastDosage?.DosageUnit || this.Model.Dosages.some(p => p.DosageUnit !== lastDosage.DosageUnit))
            return this.resetDispensationQuantities();

        if (this.Model.Dosages.count() === 1) {
            this.resetDispensationQuantities();
            this.Model.Dosages.mutate(
                p => {
                    if (lastDosage.DisplayMode == MedicationDosageDisplayMode.Quantity) {
                        const durationInDays = MedicalItemMedicationAndPrescriptionDosage.calculateTotalDuration(lastDosage);
                        p.Duration = durationInDays ? this.DaysToTicks(durationInDays) : 0;
                    }
                    else
                        p.DosageServedQuantity = this.calculateRefillQuantity(lastDosage);
                },
                p => p.Id === lastDosage.Id
            );
        }
        else {
            const lastDosageUnit = cacheStore.UnitsCache.firstOrDefault(p => p.Id === lastDosage.DosageUnit);
            const localizedDosageUnit = Unit.getLocalizedCode(lastDosageUnit);

            this.Model.RefillQuantity = this.calculateRefillQuantity(lastDosage, localizedDosageUnit);

            let firstDispensationQuantity = 0;

            for (let dosage of this.Model.Dosages.toArray())
                firstDispensationQuantity += MedicalItemMedicationAndPrescriptionDosage.calculateDispensationQuantity(dosage);

            this.Model.InitialRefillQuantity = firstDispensationQuantity ? `${firstDispensationQuantity} ${localizedDosageUnit}` : "";
        }
    }

    public AddNewDosage(): void {
        const lastDosage = this.Model.Dosages.get(this.Model.Dosages.count() - 1);
        if (lastDosage.IsFakeDosage) { // unfake dosage
            this.Model.Dosages = this.Model.Dosages
                .map(dosage => {
                    dosage.IsFakeDosage = false;
                    return dosage;
                })
                .toList();
        }
        else {
            // Add another dosage
            const cleanCopyOflastDosage = this.Model.Dosages
                .filter(dosage => dosage.Id == lastDosage.Id)
                .map(
                    dosage => new MedicalItemMedicationAndPrescriptionDosage({
                        Id: GuidHelper.NewGuid(),
                        RankPosition: dosage.RankPosition + 1,
                        DosageString: dosage.DosageString ?? "1",
                        DosageFrequency: dosage.DosageFrequency,
                        DosageUnit: dosage.DosageUnit,
                        DosageTypeLookupId: dosage.DosageTypeLookupId,
                        DosageTypeLookupValue: dosage.DosageTypeLookupValue,
                        MedicationId: dosage.MedicationId,
                        Duration: (isNaN(dosage.Duration) || dosage.Duration == 0) ? 30 * 864000000000 : dosage.Duration,
                        DosageServedQuantity: dosage.DosageServedQuantity,
                        DosageServedDays: dosage.DosageServedDays,
                        DosageServedFrequency: dosage.DosageServedFrequency,
                        DisplayMode: dosage.DisplayMode,
                        IfNeeded: dosage.IfNeeded
                    })
                )
                .first();

            this.Model.Dosages = this.Model.Dosages.push(cleanCopyOflastDosage);
        }
        this.RecalculateTotalDuration();
    }

    public DeleteDosage(dosageId: string): void {
        let newDosageItems = Immutable.List<MedicalItemMedicationAndPrescriptionDosage>();
        if (this.Model.Dosages.count() > 1) {
            newDosageItems = this.Model.Dosages
                .filter(dosage => dosage.Id != dosageId)
                .map(dosage => {
                    dosage.IsFakeDosage = false;
                    return dosage;
                })
                .toList(); // Remove item
        }
        else { // never remove the last dosage, transform it to fake
            newDosageItems = this.Model.Dosages
                .map(dosage => {
                    if (dosage.Id == dosageId) {
                        dosage.IsFakeDosage = true;
                        dosage.DosageString = "";
                        dosage.DosageFrequency = null;
                        dosage.DosageUnit = null;
                    }
                    return dosage;
                })
                .toList();

            this.Model.Route = null;
        }

        this.Model.Dosages = Immutable.List(newDosageItems);
        this.RecalculateTotalDuration();
    }

    public IsFutureStartDate(): boolean {
        return !this.Model.RefillStartDate
            || moment(this.Model.RefillStartDate).isAfter(moment(Date.now()).format());
    }

    public IsFutureEndDate(): boolean {
        const effectiveEndDate = this.EffectiveEndDate();
        return !effectiveEndDate
            || moment(effectiveEndDate).isAfter(moment(Date.now()));
    }

    public GetName(ShowForm: boolean = true, removeDsqFormInName: boolean = false): string {
        let form = "";
        let name = this.Model.Name;

        if (ShowForm == true)
            form = (this.Model.Form != null ? " (" + this.Model.Form + ")" : "");

        const drugStrength = this.Model.DrugStrength != null ? " " + this.Model.DrugStrength : "";

        if (removeDsqFormInName) {
            // Remove from Name and Transfert to FormCode **Add a Dynamic DSQ Form Mapping Tabl in SQL medfarDictionary
            name = name
                .replace("Capsule", "")
                .replace("Comprime", "")
                .replace("Gte Opht.", "")
                .replace("Sachet Ent.", "")
                .replace("Inj.seringue", "")
                .replace("Creme", "");
        }

        return `${name}${drugStrength}${form}`;
    }

    public GetInstruction(): string {
        return this.Model.Instruction ?? "";
    }

    public getReasonWithLabel(): string {
        const reason = this.getReason();

        if (reason.length > 0) {
            return `${cacheStore.GetString("ReasonLabel")}: ${reason}`;
        }

        return "";
    }

    public isInterrupted(): boolean {
        return this.Model.PrescriptionTab_PendingStatus !== null
               && (this.Model.PrescriptionTab_PendingStatus as PrescriptionMedicationStatus) === PrescriptionMedicationStatus.Interrupted;
    }

    public getLabeledCeaseDate(): string {
        return this.Model.CeaseDate
            ? `${cacheStore.GetString("DateOf")} ${this.Model.CeaseDate.format("YYYY-MM-DD")}`
            : "";
    }

    public GetNameForChildMedication(includeAction: boolean): string {
        if (this.PrescriptionTab_OriginalMedication == null)
            return "";
        else if (includeAction == true)
            return this.PrescriptionTab_OriginalMedication.GetName(false)
                + " (" + MedicationModel.PrescriptionStatusIntToValue(Number(this.PrescriptionTab_OriginalMedication.Model.PrescriptionStatusInt)) + ")";
        else
            return this.PrescriptionTab_OriginalMedication.GetName(false);
    }

    public GetNameWithStatus(currentPrescriptionId: string, PrescriptionDate: Date): string {
        if (this.isCeasedMedInSpecificPrescription(currentPrescriptionId, PrescriptionDate)) {
            return this.GetName(false) + " (" + this.PrescriptionStatusIntValue() + ".)";
        }
        else
            return this.GetName(false);
    }

    public PrescriptionStatusIntValue(): string {
        return MedicationModel.PrescriptionStatusIntToValue(this.Model.PrescriptionStatusInt);
    }

    public static PrescriptionStatusIntToValue(prescriptionStatusInt: number): string {
        switch (prescriptionStatusInt.toString() as PrescriptionMedicationStatus) {
            case PrescriptionMedicationStatus.Active:
                return cacheStore.GetString("Active3Label");

            case PrescriptionMedicationStatus.Interrupted:
                return cacheStore.GetString("InterruptedLabel");

            case PrescriptionMedicationStatus.Augmented:
                return cacheStore.GetString("AugmentedLabel");

            case PrescriptionMedicationStatus.Reduced:
                return cacheStore.GetString("ReducedLabel");

            case PrescriptionMedicationStatus.Suspended:
                return cacheStore.GetString("SuspendedLabel");

            case PrescriptionMedicationStatus.Cancelled:
                return cacheStore.GetString("CancelledLabel");

            case PrescriptionMedicationStatus.Completed:
                return cacheStore.GetString("Completed2Label");

            default:
                return "";
        }
    }

    public static PatientMedicationActionFromEnum(patientMedicationAction: PatientMedicationAction): string {
        switch (patientMedicationAction) {
            case PatientMedicationAction.MarkAsInterrupted:
                return cacheStore.GetString("InterruptedLabel");

            case PatientMedicationAction.AugmentAndReplace:
                return cacheStore.GetString("AugmentedLabel");

            case PatientMedicationAction.ReduceAndReplace:
                return cacheStore.GetString("ReducedLabel");

            case PatientMedicationAction.MarkAsSuspended:
                return cacheStore.GetString("SuspendedLabel");

            case PatientMedicationAction.MarkAsCancelled:
                return cacheStore.GetString("CancelledLabel");

            case PatientMedicationAction.MarkAsCompleted:
                return cacheStore.GetString("Completed2Label");

            case PatientMedicationAction.CompleteAndReplace:
                return cacheStore.GetString("UpdateLabel");

            default:
                return "";
        }
    }

    public getDosageUnitValue(dosageId: string): string {
        const currentDosage = this.GetDosageById(dosageId);

        if (currentDosage) {
            let returnValue = "";

            const units = cacheStore.UnitsCache
                .filter(unit => unit.Id == currentDosage.DosageUnit)
                .map(unit => {
                    if (unit.Id == 2) {
                        unit.CodeEn = "A.D.";
                        unit.CodeFr = "S.P.";
                        return unit;
                    }
                    else {
                        return unit;
                    }
                });

            if (units != null && units.length > 0)
                returnValue += Unit.getLocalizedCode(units[0]);

            const dosageTypeLK = currentDosage.DosageTypeLookupId != null ? cacheStore.GetLookupById(currentDosage.DosageTypeLookupId) : cacheStore.FindLookupByLookupCode("DU.PerDose", "DosageType");
            if (dosageTypeLK != null) {
                if (dosageTypeLK.LookupCode == "DU.PerDay")
                    returnValue += "/" + cacheStore.GetString("DaysLabel").substr(0, 1).toLowerCase();
                else if (dosageTypeLK.LookupCode == "DU.PerMinute")
                    returnValue += "/min";
                else if (dosageTypeLK.LookupCode == "DU.PerHour")
                    returnValue += "/h";
            }

            return returnValue;
        }

        return "";
    }

    public GetFrequencyValueReduced(dosageId: string): string {
        const currentDosage = this.GetDosageById(dosageId);
        if (currentDosage) {
            let returnValue = "";

            const dosageTypeLK = currentDosage.DosageTypeLookupId
                ? cacheStore.GetLookupById(currentDosage.DosageTypeLookupId)
                : cacheStore.FindLookupByLookupCode("DU.PerDose", "DosageType");

            if (dosageTypeLK != null) {
                if (dosageTypeLK.LookupCode == "DU.PerDay")
                    returnValue += cacheStore.GetString("DividedLabel") + " ";
                else if (dosageTypeLK.LookupCode == "DU.PerMinute")
                    returnValue += cacheStore.GetString("MedicationTakenLabel") + " ";
                else if (dosageTypeLK.LookupCode == "DU.PerHour")
                    returnValue += cacheStore.GetString("MedicationTakenLabel") + " ";
            }

            let codeToDisplay = currentDosage.DosageFrequency;
            if (codeToDisplay === "QD" && sessionStore.getCurrentEmployee().isActiveSiteQuebec())
                codeToDisplay = "DIE";

            returnValue += !!codeToDisplay
                ? codeToDisplay
                    .replace("weeks", "wk.")
                    .replace("week", "wk.")
                    .replace("months", "mo.")
                    .replace("month", "mo.")
                : "";

            return returnValue;
        }
        return "";
    }

    public GetDosageById(dosageId: string): MedicalItemMedicationAndPrescriptionDosage {
        const currentDosage = this.Model.Dosages.filter(dosage => dosage.Id == dosageId);
        return currentDosage.count() > 0
            ? currentDosage.get(0)
            : null;
    }

    public GetLastDosage(): MedicalItemMedicationAndPrescriptionDosage {
        const lastIndex = this.Model.Dosages.count() - 1;
        return this.Model.Dosages
            .filter((_, idx) => lastIndex === idx)
            .first();
    }

    // This function was duplicated in src/Flowsheets/Helpers/MedicationDirectivesHelper so it can be used independently of the MedicationModel
    private formatDosage(dosage: string): string {
        if (!sessionStore.getCurrentEmployee().isActiveSiteQuebec() && dosage.contains("DIE"))
            return dosage.replace("DIE", "QD");

        return dosage;
    }

    // This function was duplicated in src/Flowsheets/Helpers/MedicationDirectivesHelper so it can be used independently of the MedicationModel
    public GetDosagesDisplay(options?: Partial<DosageDisplayOptions>): string[] {
        options = new DosageDisplayOptions(options);

        const dosageDisplayArray: string[] = [];
        const lastDosageIndex = this.Model.Dosages.count() - 1;

        this.Model.Dosages.forEach((dosage, idx) => {
            const dosageUnit: string = this.getDosageUnitValue(dosage?.Id);

            let posologie = "";
            if (!dosage.IsFakeDosage) {
                const addPRN = dosage.IfNeeded ? " PRN" : "";
                const dosageStringClean = dosage.DosageString ?? "";
                posologie = `${dosageStringClean} ${dosageUnit} ${this.GetFrequencyValueReduced(dosage.Id)}${addPRN}`;
            }

            if (options.includeDaysOrQty || this.IsNarcotic()) {
                const dosageServedQuantityClean = dosage.DosageServedQuantity
                    ? `#${dosage.DosageServedQuantity} ${!isNaN(dosage.DosageServedQuantity as any) ? dosageUnit : ""}`
                    : "";

                const day = this.TicksToDays(dosage.Duration) == 0
                    ? ""
                    : this.TicksToDays(dosage.Duration).toString() + cacheStore.GetString("DaysLabel").substr(0, 1).toLowerCase();

                const dayOrQty = dosage.DisplayMode == MedicationDosageDisplayMode.Quantity || this.IsNarcotic()
                    ? dosageServedQuantityClean
                    : day;

                if (dayOrQty)
                    posologie += ` ${dayOrQty}`;
            }

            if (
                idx === lastDosageIndex
                && options.includeRefillQuantity
                && dosage.DisplayMode !== MedicationDosageDisplayMode.Quantity
            ) {
                if (this.Model.RefillQuantity)
                    posologie += ` (#${this.Model.RefillQuantity})`;
                else if (dosage.DosageServedQuantity)
                    posologie += ` (#${dosage.DosageServedQuantity} ${!isNaN(dosage.DosageServedQuantity as any) ? this.getDosageUnitValue(dosage.Id) : ""})`;
            }

            if (options.includeRenewalsOnLastDosage && idx == lastDosageIndex)
                posologie += ` ${this.GetRenewalString(options)}`;

            dosageDisplayArray.push(this.formatDosage(posologie).trim());

            // served every...
            if (this.IsNarcotic() && options.includeNarcoticFreq) {
                let servedFreq = PatientMedicalItemsHelper.GetMedicationNarcoticFrequency(dosage);
                const servedQty = PatientMedicalItemsHelper.GetMedicationNarcoticQuantity(dosage, dosageUnit);

                if (servedFreq != "")
                    servedFreq += ", ";

                dosageDisplayArray.push(this.formatDosage(`${servedFreq}${servedQty}`).trim());
            }
        });

        return dosageDisplayArray;
    }

    public getFirstDispensationString(): string {
        if (!IsNullOrWhiteSpace(this.Model.InitialRefillQuantity)) {
            return `${cacheStore.GetString("FirstDispensationLabel")} #${this.Model.InitialRefillQuantity}`;
        }
        return null;
    }

    /**
     * Convert date ticks to an equivalent number of days, rounded to the nearest day.
     * This function was duplicated in src/Flowsheets/Helpers/MedicationDirectivesHelper so it can be used independently of the MedicationModel
     * @param ticks The number of date ticks.
     * @param applyIndefiniteDuration If this medication has an indefinite duration, whether to return 0 regardless of the input value.
     */
    public TicksToDays(ticks: number, applyIndefiniteDuration: boolean = true): number {
        const days = Number(ticks / Number(864000000000));

        if (applyIndefiniteDuration && this.Model.TotalDurationManual == null)
            return 0;

        return Math.round(days);
    }

    /**
     * Convert days to an equivalent number of date ticks.
     * @param days The number of days.
     */
    public DaysToTicks(days: number | string): number {
        return Number(days) * 864000000000;
    }

    // This function was duplicated in src/Flowsheets/Helpers/MedicationDirectivesHelper so it can be used independently of the MedicationModel
    public GetDosageString(options?: Partial<DosageDisplayOptions>): string {
        const val = this.Model;

        if (!!val) {
            if (val.Dosages == null)
                return "...";
            else if (val.Dosages.count() == 0)
                return "";
            else
                return this.GetDosagesDisplay({ ...options, includeRenewalsOnLastDosage: false }).pop();
        }

        return "";
    }

    public GetOriginString(): string {
        const val = this.Model;

        let result = "";
        if (val != null && val != undefined) {
            if (val.DsqId && val.DsqId.length > 0) {
                result = "DSQ";

                if (val.IsPast)
                    result = "RxDSQ";
                else
                    result = "MYLE/DSQ";
            }
            else {
                if (val.IsPast)
                    result = "MYLE";
                else
                    result = "Rx";
            }
        }

        return result;
    }

    // This function was duplicated in src/Flowsheets/Helpers/MedicationDirectivesHelper so it can be used independently of the MedicationModel
    public GetRenewalString(options: Partial<DosageDisplayOptions> = new DosageDisplayOptions()): string {
        const i = this.Model;
        let refillString = "";
        if (i?.NumberOfRefill > 0) {
            if (i.SummaryDisplayStatus == EnumPatientSummaryMedicationDisplayStatus.ShowExpired)
                refillString = `R0/${i.NumberOfRefill}`;
            else if (this.IsCeased())
                refillString = (i.NumberOfRefill == null || i.NumberOfRefill == 0) ? "" : `R${i.NumberOfRefill.toString()}`;
            else if (i.SummaryDisplayStatus == EnumPatientSummaryMedicationDisplayStatus.ShowActive) {
                if (isNaN(i.DSQNumberOfRefillRemaining) == false) // DSQ Override
                    refillString = `R${i.DSQNumberOfRefillRemaining?.toString() ?? ""}*/${i.NumberOfRefill}`;
                else if (i.RefillEndDate == null)
                    refillString = "R" + i.NumberOfRefill;
                else {
                    const nbDaysPerRefill = i?.NumberOfRefill > 0
                        ? i.TotalDurationManualDays / i.NumberOfRefill
                        : 0;

                    let nbDaysTillRefillEnd = i?.NumberOfRefill > 0
                        ? moment(i.RefillEndDate).diff(DateTime.nowMoment(), "day")
                        : 0;

                    if (nbDaysTillRefillEnd < 0)
                        nbDaysTillRefillEnd = 0;

                    const nbRefillsLeft = (i.SummaryDisplayStatus >= 1 && i.NumberOfRefill && i.NumberOfRefill > 0 && nbDaysPerRefill > 0)
                        ? Math.ceil(nbDaysTillRefillEnd / nbDaysPerRefill)
                        : 0;

                    const nbRefillsLeftDisplay = nbRefillsLeft.toString();

                    refillString = options.includeRemainingRenewalCalculation
                        ? `R${nbRefillsLeftDisplay}/${i.NumberOfRefill}`
                        : `R${i.NumberOfRefill}`;
                }
            }
            else
                refillString = !i.NumberOfRefill ? "" : `R${i.NumberOfRefill}`;
        }
        return refillString;
    }

    public GetStatusString(): string {
        const model = this.Model;

        if (model) {
            const prescriptionStatus = model.PrescriptionStatusInt.toString() as PrescriptionMedicationStatus;

            switch (prescriptionStatus) {
                case PrescriptionMedicationStatus.Active:
                case PrescriptionMedicationStatus.Updated:
                    switch (model.SummaryDisplayStatus) {
                        case EnumPatientSummaryMedicationDisplayStatus.ShowActive:
                            return cacheStore.GetString("ActiveLabel");

                        case EnumPatientSummaryMedicationDisplayStatus.ShowExpired:
                            return cacheStore.GetString("ExpiredLabel");

                        default:
                            return cacheStore.GetString("CompletedLabel");
                    }
                case PrescriptionMedicationStatus.Interrupted:
                    return cacheStore.GetString("InterruptedLabel");

                case PrescriptionMedicationStatus.Augmented:
                    return cacheStore.GetString("AugmentedLabel");

                case PrescriptionMedicationStatus.Reduced:
                    return cacheStore.GetString("ReducedLabel");

                case PrescriptionMedicationStatus.Suspended:
                    return cacheStore.GetString("SuspendedLabel");

                case PrescriptionMedicationStatus.Cancelled:
                    return cacheStore.GetString("CancelledLabel");

                case PrescriptionMedicationStatus.Completed:
                    return cacheStore.GetString("CompletedLabel");
            }
        }

        return "";
    }

    // This function was duplicated in src/Flowsheets/Helpers/MedicationDirectivesHelper so it can be used independently of the MedicationModel
    public GetDurationInDaysString(): string {
        return this.Model.TotalDurationManualDays
            ? `${this.Model.TotalDurationManualDays} ${cacheStore.GetString("DaysLabel").toLowerCase()}`
            : "";
    }

    public IsCeased(): boolean {
        return [
            PrescriptionMedicationStatus.Interrupted,
            PrescriptionMedicationStatus.Augmented,
            PrescriptionMedicationStatus.Reduced,
            PrescriptionMedicationStatus.Suspended,
            PrescriptionMedicationStatus.Cancelled
        ].contains(this.Model.PrescriptionStatusInt?.toString());
    }

    public SetModeForDosage(dosageId: string, mode: MedicationDosageDisplayMode, unit: MedicationDosageDisplayModeDurationUnit = MedicationDosageDisplayModeDurationUnit.Days): void {
        if (!unit)
            unit = MedicationDosageDisplayModeDurationUnit.Days;

        this.mutateDosage(dosageId, dosage => {
            if (dosage.DisplayMode !== mode && mode === MedicationDosageDisplayMode.Quantity) {
                dosage.DosageServedQuantity = this.calculateRefillQuantity(dosage);
                dosage.Duration = 0;
            }
            else if (dosage.DisplayMode !== mode && mode === MedicationDosageDisplayMode.Duration && !this.IsNarcotic()) {
                const durationInDays = MedicalItemMedicationAndPrescriptionDosage.calculateTotalDuration(dosage);
                dosage.Duration = durationInDays ? this.DaysToTicks(durationInDays) : 0;
                dosage.DosageServedQuantity = "";
            }

            dosage.DisplayMode = mode;
            dosage.DisplayModeDurationUnit = unit;

            if (mode == MedicationDosageDisplayMode.EndDate) {
                dosage.Duration = 0;
                dosage.DosageServedQuantity = "";
            }
        });

        if (mode == MedicationDosageDisplayMode.EndDate) {
            this.Model.NumberOfRefill = null;
        }

        this.RecalculateTotalDuration();
        this.recalculateDispensationQuantities();
    }

    public mutateDosage(dosageId: string, call: (model: MedicalItemMedicationAndPrescriptionDosage, x?: MedicationModel) => void): void {
        if (this.Model.Dosages == null)
            return;

        this.Model.Dosages = this.Model.Dosages
            .map(y => {
                if (y.Id == dosageId)
                    call(y, this);

                return y;
            })
            .toList();
    }

    // This function was duplicated in src/Flowsheets/Helpers/MedicationDirectivesHelper
    public getMedicationQuantities(): string[] {
        const validateDosageQuantity = (dosage: MedicalItemMedicationAndPrescriptionDosage): boolean =>
            dosage.DosageServedQuantity && !isNaN(Number(dosage.DosageServedQuantity));

        if (this.Model.Dosages.count() > 1) {
            const lastDose = this.Model.Dosages.last();
            const quantityUnit = validateDosageQuantity(lastDose) ? this.getDosageUnitValue(lastDose.Id) : "";

            const refillQuantity = lastDose.DisplayMode === MedicationDosageDisplayMode.Quantity
                ? lastDose.DosageServedQuantity + " " + quantityUnit
                : this.Model.RefillQuantity;

            const initialRefillQuantity = this.Model.InitialRefillQuantity;
            if (!IsNullOrWhiteSpace(initialRefillQuantity)) {
                const initialRefillQuantityInString: string = `${cacheStore.GetString("FirstDispensationShortLabel")}: ${initialRefillQuantity}`;

                if (refillQuantity) {
                    const refillQuantityInString = `${cacheStore.GetString("RefillQuantityAcronymLabel")}: ${refillQuantity}`;
                    return [initialRefillQuantityInString, refillQuantityInString];
                }
                else {
                    return [initialRefillQuantityInString];
                }
            }

            return [refillQuantity];
        }
        else if (this.Model.Dosages.count() === 1) {
            const dosage = this.Model.Dosages.first();
            if (!IsNullOrWhiteSpace(dosage.DosageServedQuantity))
                return [`${dosage.DosageServedQuantity} ${validateDosageQuantity(dosage) ? this.getDosageUnitValue(dosage.Id) : ""}`];
        }

        return [];
    }

    public isLongTerm(): boolean {
        return cacheStore.ClinicSettings.EnableLongTermMedication && this.Model.IsLongTerm === true;
    }

    public isExpiredLongTermMed(): boolean {
        return (
            this.isLongTerm()
            && this.Model.SummaryDisplayStatus === EnumPatientSummaryMedicationDisplayStatus.ShowExpired
        );
    }

    private resetDispensationQuantities(): void {
        if (this.Model.Dosages.count() === 1) {
            if (this.Model.Dosages.first().DisplayMode == MedicationDosageDisplayMode.Quantity)
                this.Model.Dosages.mutate(dosage => dosage.Duration = 0);
            else
                this.Model.Dosages.mutate(dosage => dosage.DosageServedQuantity = "");
        }
        this.Model.RefillQuantity = "";
        this.Model.InitialRefillQuantity = "";
    }

    private calculateRefillQuantity(dosage: MedicalItemMedicationAndPrescriptionDosage, unit?: string): string {
        const refillQuantity = MedicalItemMedicationAndPrescriptionDosage.calculateDispensationQuantity(dosage);
        return refillQuantity ? `${refillQuantity}${unit ? ` ${unit}` : ""}` : "";
    }

    public static shouldShowAndCalculateDispensationQuantities(isNarcotic: boolean): boolean {
        return settingsStore.configuration.get("ShouldShowAndCalculateMedicationDispensationQuantities") && !isNarcotic;
    }

    public static shouldShowFirstDispensationQuantity(isNarcotic: boolean, dosageCount: number): boolean {
        return MedicationModel.shouldShowAndCalculateDispensationQuantities(isNarcotic) && dosageCount > 1;
    }

    public static shouldShowRefillQuantity(isNarcotic: boolean, dosageCount: number, lastDosageDisplayMode: MedicationDosageDisplayMode): boolean {
        return MedicationModel.shouldShowFirstDispensationQuantity(isNarcotic, dosageCount) && lastDosageDisplayMode !== MedicationDosageDisplayMode.Quantity;
    }

    public static shouldShowSingleDosageQuantityInsteadOfDuration(dosageCount: number, lastDosageDisplayMode: MedicationDosageDisplayMode): boolean {
        return dosageCount === 1 && lastDosageDisplayMode !== MedicationDosageDisplayMode.Quantity;
    }

    public shouldShowOriginalMedicationReason(): boolean {
        return this.PrescriptionTab_OriginalMedication != null && (
            !IsNullOrWhiteSpace(this.PrescriptionTab_OriginalMedication.Model.CeaseReason)
            || (cacheStore.ClinicSettings.EnableMedicationDiscontinueReason && this.PrescriptionTab_OriginalMedication.Model.DiscontinueReasonLookupId != null)
        );
    }

    public getOriginalMedicationReason(): string {
        const discontinueReason = cacheStore.ClinicSettings.EnableMedicationDiscontinueReason && this.PrescriptionTab_OriginalMedication.Model.DiscontinueReasonLookupId != null
            ? cacheStore.GetLocalizedValueByLookupId(this.PrescriptionTab_OriginalMedication.Model.DiscontinueReasonLookupId).trim()
            : "";

        const ceaseReason = !IsNullOrWhiteSpace(this.PrescriptionTab_OriginalMedication.Model.CeaseReason)
            ? this.PrescriptionTab_OriginalMedication.Model.CeaseReason.trim()
            : "";

        return `${discontinueReason} ${ceaseReason}`.trim();
    }

    public getReason(): string {
        const discontinueReason = cacheStore.ClinicSettings.EnableMedicationDiscontinueReason && this.Model.DiscontinueReasonLookupId != null
            ? cacheStore.GetLocalizedValueByLookupId(this.Model.DiscontinueReasonLookupId).trim()
            : "";

        const ceaseReason = !IsNullOrWhiteSpace(this.Model.CeaseReason)
            ? this.Model.CeaseReason.trim()
            : "";

        return `${discontinueReason} ${ceaseReason}`.trim();
    }

    public static getSearchedMedicationItem(medication: MedicationModel, response: MedicationSearchListItem): MedicationModel {
        const cloneBase = medication.Clone(true, true);
        cloneBase.ClearIdentificationFields();
        cloneBase.FromResponseLightUpdate(response.responseItem, true);

        resolveMedication(
            response.responseItem,
            cloneBase.Model.Id,
            medication => cloneBase.FromResponseFullUpdate(medication)
        );

        cloneBase.Model.IsPast = false; // Is a prescription
        cloneBase.Model.OriginalMedicationId = medication.Model.Id;

        return cloneBase;
    }

    public static clearSearchedMedication(medication: MedicationModel): MedicationModel {
        const clone = medication.Clone(false, false, true);
        clone.ClearIdentificationFields();
        clone.Model.Name = "undefined";

        return clone;
    }

    public static setAdjustMedication(
        medicationModel: MedicationModel,
        selectedCode: string,
        ceaseDate: Date,
        ceaseReason: string,
        discontinueReasonLookupId: string | null,
        ceaseEmployeeId: Guid,
        ceaseEncounterId: Guid
    ): MedicationModel {
        const medicationClone = medicationModel.Clone();

        medicationClone.IsInEditMode = false;
        medicationClone.Model.PrescriptionTab_PendingStatus = selectedCode;

        // medicationClone.Model.Dosages = [].toImmutable();
        // medicationClone.CreateFakeDosage();
        // medicationClone.AddNewDosage();

        medicationClone.Model.CeaseDate = ceaseDate;
        medicationClone.Model.CeaseReason = ceaseReason;
        medicationClone.Model.DiscontinueReasonLookupId = discontinueReasonLookupId;
        medicationClone.Model.CeaseEmployeeId = ceaseEmployeeId;
        medicationClone.Model.CeaseEncounterId = ceaseEncounterId;

        return medicationClone;
    }

    public static convertImmutableMedicationModelToRaw(medicationModel: MedicationModel): MedicationModel | { Model: { Dosages: MedicalItemMedicationAndPrescriptionDosage[] } } {
        return { ...medicationModel, Model: ({ ...medicationModel.Model, Dosages: medicationModel.Model.Dosages.toArray() }) };
    }

    /**
     * Sort the same reference's medication model by EMRTemplateSortIndex
     * @param {MedicationModel[]} medicationModels
     */
    public static sortMedicationModelsByEMRTemplateSortIndex(medicationModels: MedicationModel[]): void {
        medicationModels.sort((a, b) => {
            if (a.EMRTemplateSortIndex > b.EMRTemplateSortIndex)
                return 1;

            if (a.EMRTemplateSortIndex < b.EMRTemplateSortIndex)
                return -1;

            return 0;
        });
    }

    /**
     * Return true if the vocab is equal to Augmented, Cancelled, Interrupted, Suspended, or Reduced
     * @param vocab
     * @returns {boolean}
     */
    public static validateCeasedMedicationByVocab(vocab: string): boolean {
        return vocab === cacheStore.GetString("AugmentedLabel") ||
            vocab === cacheStore.GetString("CancelledLabel") ||
            vocab === cacheStore.GetString("InterruptedLabel") ||
            vocab === cacheStore.GetString("SuspendedLabel") ||
            vocab === cacheStore.GetString("ReducedLabel");
    }
}

class GroupedMedicationModel {
    public Items: MedicationModel[];
    public key: string;
    public MaxEndDate: Date;
}

export { GroupedMedicationModel, MedicationDosageDisplayMode, MedicationDosageDisplayModeDurationUnit, MedicationModel };
